
import React, { useState, useEffect } from 'react'
import { X, Clock, User, Edit, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { useOrcamentos } from '../hooks/useOrcamentos'

interface HistoricoOrcamentoProps {
  isOpen: boolean
  onClose: () => void
  orcamentoId: string
  orcamentoNumero: string
}

const HistoricoOrcamento: React.FC<HistoricoOrcamentoProps> = ({
  isOpen,
  onClose,
  orcamentoId,
  orcamentoNumero
}) => {
  const { historico } = useOrcamentos()
  const [filteredHistorico, setFilteredHistorico] = useState<any[]>([])
  const [filterType, setFilterType] = useState<string>('all')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (orcamentoId && historico && isOpen) {
      setLoading(true)
      
      // Filter history for this specific budget
      let filtered = historico.filter(h => h.orcamentoId === orcamentoId)
      
      if (filterType !== 'all') {
        filtered = filtered.filter(h => h.acao === filterType)
      }
      
      // Sort by date descending (most recent first)
      filtered.sort((a, b) => new Date(b.dataAcao).getTime() - new Date(a.dataAcao).getTime())
      
      setFilteredHistorico(filtered)
      setLoading(false)
    }
  }, [orcamentoId, historico, filterType, isOpen])

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return {
        date: date.toLocaleDateString('pt-BR'),
        time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      }
    } catch (error) {
      return { date: 'Data inválida', time: '' }
    }
  }

  const getActionIcon = (acao: string) => {
    switch (acao) {
      case 'criado':
        return <FileText size={16} className="text-blue-600" />
      case 'editado':
        return <Edit size={16} className="text-yellow-600" />
      case 'enviado':
        return <CheckCircle size={16} className="text-blue-600" />
      case 'aprovado':
        return <CheckCircle size={16} className="text-green-600" />
      case 'perdido':
        return <XCircle size={16} className="text-red-600" />
      case 'expirado':
        return <AlertCircle size={16} className="text-orange-600" />
      default:
        return <Clock size={16} className="text-gray-600" />
    }
  }

  const getActionColor = (acao: string) => {
    switch (acao) {
      case 'criado':
        return 'border-l-blue-500 bg-blue-50'
      case 'editado':
        return 'border-l-yellow-500 bg-yellow-50'
      case 'enviado':
        return 'border-l-blue-500 bg-blue-50'
      case 'aprovado':
        return 'border-l-green-500 bg-green-50'
      case 'perdido':
        return 'border-l-red-500 bg-red-50'
      case 'expirado':
        return 'border-l-orange-500 bg-orange-50'
      default:
        return 'border-l-gray-500 bg-gray-50'
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const getActionLabel = (acao: string) => {
    switch (acao) {
      case 'criado': return 'Criado'
      case 'editado': return 'Editado'
      case 'enviado': return 'Enviado'
      case 'aprovado': return 'Aprovado'
      case 'perdido': return 'Perdido'
      case 'expirado': return 'Expirado'
      default: return acao
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Histórico do Orçamento</h2>
              <p className="text-gray-600 mt-1">#{orcamentoNumero}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          {/* Filtros */}
          <div className="mt-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todas as ações</option>
              <option value="criado">Criação</option>
              <option value="editado">Edições</option>
              <option value="enviado">Envios</option>
              <option value="aprovado">Aprovações</option>
              <option value="perdido">Perdas</option>
              <option value="expirado">Expirações</option>
            </select>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Carregando histórico...</p>
            </div>
          ) : filteredHistorico.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum histórico encontrado</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filterType !== 'all' 
                  ? 'Tente ajustar o filtro selecionado'
                  : 'O histórico aparecerá aqui conforme ações forem realizadas'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredHistorico.map((item, index) => {
                const { date, time } = formatDate(item.dataAcao)
                
                return (
                  <div
                    key={item._id || index}
                    className={`border-l-4 p-4 rounded-r-lg ${getActionColor(item.acao)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {getActionIcon(item.acao)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900">
                              {getActionLabel(item.acao)}
                            </h4>
                            <span className="text-sm text-gray-500">
                              por {item.usuario || 'Sistema'}
                            </span>
                          </div>
                          
                          <p className="text-gray-700 mb-2">
                            {item.descricao || `Orçamento ${item.acao}`}
                          </p>

                          {/* Detalhes específicos da ação */}
                          {item.valorAnterior !== undefined && item.valorNovo !== undefined && (
                            <div className="bg-white p-2 rounded border text-sm">
                              <p>
                                <span className="text-gray-600">Valor anterior:</span>{' '}
                                <span className="line-through text-red-600">
                                  {formatCurrency(item.valorAnterior)}
                                </span>
                              </p>
                              <p>
                                <span className="text-gray-600">Novo valor:</span>{' '}
                                <span className="text-green-600 font-semibold">
                                  {formatCurrency(item.valorNovo)}
                                </span>
                              </p>
                            </div>
                          )}

                          {item.statusAnterior && item.statusNovo && (
                            <div className="bg-white p-2 rounded border text-sm">
                              <p>
                                <span className="text-gray-600">Status anterior:</span>{' '}
                                <span className="capitalize text-gray-700">
                                  {item.statusAnterior}
                                </span>
                              </p>
                              <p>
                                <span className="text-gray-600">Novo status:</span>{' '}
                                <span className="capitalize font-semibold text-blue-600">
                                  {item.statusNovo}
                                </span>
                              </p>
                            </div>
                          )}

                          {item.observacoes && (
                            <div className="bg-white p-2 rounded border text-sm mt-2">
                              <p className="text-gray-600">Observações:</p>
                              <p className="text-gray-700">{item.observacoes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <User size={12} />
                          <span>{item.usuario || 'Sistema'}</span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <Clock size={12} />
                          <span>{date} {time && `às ${time}`}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HistoricoOrcamento
